import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { getDownloadInfo, getOpenShareAppUrl } from "../utils/storeLinks";

const AUTO_OPEN_DELAY_MS = 350;
const FALLBACK_DELAY_MS = 1400;

function OpenApp() {
  const download = getDownloadInfo();
  const appUrl = useMemo(() => getOpenShareAppUrl(), []);
  const fallbackTimerRef = useRef(null);
  const [showFallback, setShowFallback] = useState(false);
  const [attemptedOpen, setAttemptedOpen] = useState(false);

  const openBite = useCallback(() => {
    setAttemptedOpen(true);
    setShowFallback(false);
    window.location.href = appUrl;

    if (fallbackTimerRef.current) {
      window.clearTimeout(fallbackTimerRef.current);
    }
    fallbackTimerRef.current = window.setTimeout(() => {
      if (!document.hidden) {
        setShowFallback(true);
      }
    }, FALLBACK_DELAY_MS);
  }, [appUrl]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoOpen") === "0") {
      setShowFallback(true);
      return undefined;
    }

    const timer = window.setTimeout(openBite, AUTO_OPEN_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      if (fallbackTimerRef.current) {
        window.clearTimeout(fallbackTimerRef.current);
      }
    };
  }, [openBite]);

  return (
    <Page>
      <Panel>
        <Eyebrow>Open Bite</Eyebrow>
        <Title>Stop losing recipes.</Title>
        <Copy>
          Tap below to open Bite. Next time you see a recipe on Instagram,
          TikTok, YouTube, or a website, share it straight to Bite.
        </Copy>
        <PrimaryButton href={appUrl} onClick={openBite}>
          Open Bite
        </PrimaryButton>
        <Fallback $visible={showFallback || attemptedOpen}>
          <FallbackText>
            If Bite does not open, make sure the app is installed.
          </FallbackText>
          <SecondaryButton href={download.url} rel="noreferrer">
            {download.label}
          </SecondaryButton>
        </Fallback>
      </Panel>
    </Page>
  );
}

const Page = styled.main`
  min-height: calc(100vh - 174px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 54px 24px 36px;
  background: #f6f7fb;

  @media (max-width: 590px) {
    min-height: calc(100vh - 132px);
    padding: 34px 18px 28px;
  }
`;

const Panel = styled.section`
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border: 1px solid #ebecef;
  border-radius: 18px;
  padding: 38px;
  box-shadow: 0 18px 48px rgba(17, 24, 39, 0.08);

  @media (max-width: 590px) {
    padding: 26px 22px;
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(17, 24, 39, 0.08);
  }
`;

const Eyebrow = styled.div`
  color: #6c8e7d;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 10px;
`;

const Title = styled.h1`
  color: #111111;
  font-size: 36px;
  line-height: 1.12;
  letter-spacing: 0;
  margin: 0 0 14px;

  @media (max-width: 590px) {
    font-size: 28px;
    line-height: 1.14;
    margin-bottom: 12px;
  }
`;

const Copy = styled.p`
  color: #3f3f46;
  font-size: 18px;
  line-height: 1.5;
  margin: 0 0 24px;

  @media (max-width: 590px) {
    font-size: 16px;
    line-height: 1.48;
    margin-bottom: 22px;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0 26px;
  border-radius: 999px;
  background: #111111;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;

  @media (max-width: 590px) {
    min-height: 48px;
    padding: 0 24px;
  }
`;

const Fallback = styled.div`
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #eeeeee;
`;

const FallbackText = styled.p`
  color: #52525b;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 14px;
`;

const SecondaryButton = styled.a`
  color: #111111;
  font-size: 15px;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

export default OpenApp;
